import {
  Alert,
  BodyText,
  Card,
  Flex,
  Heading,
  InlineLink,
  RadioGroup,
  RadioTile,
  TextInput,
  ToggleGroup,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import { LaunchMediumIcon } from '@utilitywarehouse/hearth-react-icons';
import { useFormikContext } from 'formik';

import { CURRENCY_CODE } from '@constants/app';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { useFieldProps } from '@hooks/useFieldProps';
import { BroadbandFormValues, CallPackages } from '@src/types/broadband';
import { BooleanSelection } from '@src/types/index';
import { formatCurrency } from '@utils/format';

import { CallPackageTile } from '../CallPackageTile';
import '../styles.scss';

interface LineActivationProps {
  showDigitalHomePhone?: Nullable<boolean>;
  isOptionalVoipEnabled?: boolean;
}

const LineActivation = ({
  showDigitalHomePhone = false,
  isOptionalVoipEnabled = false,
}: LineActivationProps) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    handleBlur,
    submitCount,
  } = useFormikContext<BroadbandFormValues>();

  const packageMock = [
    {
      serviceName: 'Off-peak Saver',
      id: CallPackages.offPeakSaver,
      price: { value: '6.50', currency: CURRENCY_CODE },
      description:
        'Unlimited free calls (up to 75 mins each) to 01, 02, 03 numbers and UK mobiles from 7pm-7am during the week, and all day weekends',
    },
    {
      serviceName: 'Peak Saver',
      id: CallPackages.peakSaver,
      price: { value: '13.00', currency: CURRENCY_CODE },
      description:
        'Unlimited free calls (up to 75 mins each) to 01, 02, 03 numbers and UK mobiles any time',
    },
  ];

  const getFieldProps = useFieldProps<BroadbandFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const landlineInputProps = getFieldProps('landline');
  const includeVoipProps = getFieldProps('includeVoip');
  const showVoipPicker = isOptionalVoipEnabled && showDigitalHomePhone;
  const voipExcluded =
    showVoipPicker && values.includeVoip === BooleanSelection.No;
  const showHomePhoneDetails =
    !showVoipPicker || values.includeVoip === BooleanSelection.Yes;

  return (
    <div className="wrapper">
      <Flex asChild direction="column" spacing="xl">
        <section className="broadband__LineActivation__wrapper">
          {!showVoipPicker && (
            <Flex asChild direction="column" spacing="lg">
              <header>
                <div>
                  <Heading
                    className="heading"
                    as="h2"
                    size="lg"
                    data-testid="broadband-homephone-title"
                  >
                    {showDigitalHomePhone
                      ? 'Digital home phone'
                      : 'Your line activation'}
                  </Heading>
                  {showDigitalHomePhone && (
                    <Flex columnGap="50" marginTop="200" asChild>
                      <BodyText size="lg" weight="semibold">
                        <LaunchMediumIcon />
                        Included for free with your broadband plan
                      </BodyText>
                    </Flex>
                  )}
                </div>
                <BodyText>
                  {showDigitalHomePhone ? (
                    <>
                      A digital home phone runs over your full fibre broadband
                      connection rather than a traditional copper phone line.
                      You can even keep the same phone number and handset if you
                      already have a landline.
                    </>
                  ) : (
                    <>
                      If you already have a landline and you’d like to keep your
                      number, enter it below and we’ll transfer it if we can. If
                      we can’t transfer it, we’ll activate a new line.
                    </>
                  )}
                </BodyText>
              </header>
            </Flex>
          )}
          {showVoipPicker && (
            <Card
              direction="column"
              variant="emphasis"
              colorScheme="neutralStrong"
              spacing="xl"
            >
              <RadioGroup
                {...includeVoipProps}
                label={
                  <Heading className="heading" size="md">
                    Do you need a digital home phone with your broadband?
                  </Heading>
                }
                value={values.includeVoip ?? ''}
                onValueChange={(val: string) =>
                  setFieldValue('includeVoip', val)
                }
                direction="row"
                data-testid="broadband-include-voip-radio-buttons"
              >
                <RadioTile
                  value={BooleanSelection.Yes}
                  label="Yes"
                  id="includeVoipYes"
                />
                <RadioTile
                  value={BooleanSelection.No}
                  label="No (broadband only)"
                  id="includeVoipNo"
                />
              </RadioGroup>
              {voipExcluded && (
                <Alert colorScheme="info">
                  <BodyText weight="semibold" size="md">
                    The below services will not function over a data only Full
                    Fibre connection:
                  </BodyText>
                  <BodyText size="sm">
                    &#8226; Home phone services, including the ability to make
                    emergency calls.
                  </BodyText>
                  <BodyText size="sm">
                    &#8226; Any services that currently use your phone line
                    (such as medical equipment, telecare devices or care alarms
                    or burglar alarms).
                  </BodyText>
                  <BodyText size="sm">
                    &#8226; If you currently have a home phone number it will
                    cease to exist.
                  </BodyText>
                </Alert>
              )}
            </Card>
          )}
          {showHomePhoneDetails && (
            <Card
              direction="column"
              variant="subtle"
              colorScheme="neutralStrong"
              spacing="xl"
            >
              <Heading size="md" data-testid="broadband-homephone-details">
                {showDigitalHomePhone
                  ? 'Home phone details'
                  : 'Landline details'}
              </Heading>
              <TextInput
                label={`${showDigitalHomePhone ? 'Telephone' : 'Landline'} number`}
                {...landlineInputProps}
                id="broadband-landline"
                data-testid="broadband-landline-number"
                helperText="To keep your current number, enter it below. Not sure what it is? Check a recent bill from your current provider. Leave it blank and we'll give you a new one."
              />

              <RadioGroup
                name="callPackage"
                label="Would you like to add a call package?"
                helperText={
                  <span>
                    You can still use your{' '}
                    {showDigitalHomePhone ? 'digital home phone' : 'landline'}{' '}
                    without a call package, by paying our{' '}
                    <InlineLink
                      color="inherit"
                      href={EXTERNAL_LINKS.chargesApply}
                      rel="noreferrer noopener"
                      hideOpenIcon
                      target="_blank"
                    >
                      regular usage charges
                    </InlineLink>
                    .
                  </span>
                }
                value={values.callPackage}
                onValueChange={(val: string) =>
                  setFieldValue('callPackage', val)
                }
                data-testid="broadband-call-package-radio-buttons"
                direction="row"
              >
                <RadioTile value={BooleanSelection.Yes} label="Yes" />
                <RadioTile value={BooleanSelection.No} label="No" />
              </RadioGroup>

              {values.callPackage === BooleanSelection.Yes && (
                <>
                  {errors.callPackageId && touched.callPackageId && (
                    <ValidationText status="invalid">
                      *Please choose a call package
                    </ValidationText>
                  )}
                  <Flex
                    direction="column"
                    spacing="md"
                    data-testid="broadband-call-package-section"
                  >
                    <BodyText
                      weight="semibold"
                      aria-labelfor="callPackageId"
                      asChild
                    >
                      <label htmlFor="callPackageId">
                        Choose your call package
                      </label>
                    </BodyText>
                    <ToggleGroup
                      type="single"
                      className="broadband__PlanSelection"
                      aria-label="Choose your call package"
                      {...getFieldProps('callPackageId')}
                      id="callPackageId"
                      value={values.callPackageId ?? undefined}
                      onValueChange={(value: string) =>
                        setFieldValue('callPackageId', value)
                      }
                      direction={{ mobile: 'column', tablet: 'row' }}
                      spacing="xl"
                    >
                      {/* mocked elements, will (probably) be generated from server response */}
                      {packageMock.map((callPackage) => (
                        <CallPackageTile
                          key={callPackage.id}
                          serviceName={callPackage.serviceName}
                          price={formatCurrency(callPackage.price)}
                          description={callPackage.description}
                          value={callPackage.id}
                          isSelected={callPackage.id === values.callPackageId}
                        />
                      ))}
                    </ToggleGroup>
                  </Flex>
                </>
              )}
              <footer>
                <BodyText size="sm">
                  To help minimise unwanted sales calls, we'll add your line as
                  "ex-directory" and will register your number with the
                  Telephone Preference Service. You can change these settings at
                  any time.
                </BodyText>
              </footer>
            </Card>
          )}
        </section>
      </Flex>
    </div>
  );
};

export { LineActivation };
